import { useCallback } from "react";
import { flushSync } from "react-dom";
import { useNavigate, type NavigateOptions, type To } from "react-router-dom";

// Browsers that don't implement the View Transitions API just get a plain
// navigate() — everything else runs the flushSync + startViewTransition
// pattern so React commits synchronously between the "old" and "new" snapshots
// the browser captures.
type StartViewTransition = (cb: () => void) => { finished: Promise<void> };

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Wraps react-router's `navigate()` in a document.startViewTransition() call
 * when the browser supports it. Falls back to a plain navigate() otherwise
 * (older Safari/Firefox, or when the user prefers reduced motion).
 *
 * Optional `beforeNavigate` callback lets callers commit synchronous DOM
 * teardown (e.g. closing a drawer via flushSync) BEFORE the browser captures
 * the "old" snapshot — critical to prevent mid-animation UI from being frozen
 * into the transition frame.
 */
export function useViewTransitionNavigate() {
  const navigate = useNavigate();
  return useCallback(
    (to: To, options?: NavigateOptions & { beforeNavigate?: () => void }) => {
      const { beforeNavigate, ...navOptions } = options ?? {};
      const doc =
        typeof document !== "undefined"
          ? (document as Document & { startViewTransition?: StartViewTransition })
          : null;

      if (!doc?.startViewTransition || prefersReducedMotion()) {
        if (beforeNavigate) flushSync(beforeNavigate);
        navigate(to as string, navOptions);
        return;
      }

      // Commit pre-transition state (drawer close, etc.) synchronously so the
      // "old" snapshot is captured on the settled UI, not mid-animation.
      if (beforeNavigate) flushSync(beforeNavigate);

      doc.startViewTransition(() => {
        flushSync(() => {
          navigate(to as string, navOptions);
        });
      });
    },
    [navigate],
  );
}
